import { ArrowLeftOutlined } from '@ant-design/icons';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { isInquiryReadonlyPreview } from '@framework/common-web/ui/inquiry/isInquiryReadonlyPreview.ts';
import { TitleSubmissionRow } from '@framework/common-web/ui/inquiry/submission/TitleSubmissionRow.tsx';
import { type IModuleDelegation } from '@framework/frontoffice-submission/module/delegation/DelegationLogic.ts';
import { DelegationMachine } from '@framework/frontoffice-submission/module/delegation/DelegationMachine.ts';
import { getModuleTitle } from '@framework/frontoffice-submission/module/getModuleTitle.ts';
import { useSubmission } from '@framework/frontoffice-submission/submission/hooks/useSubmission.ts';
import { useSubmissionModules } from '@framework/frontoffice-submission/submission/hooks/useSubmissionModules.ts';
import { SubmissionStateTag } from '@framework/frontoffice-submission/submission/state/SubmissionStateTag.tsx';
import { t } from '@framework/i18n/intl/i18next.ts';
import { LinkButton } from '@framework/ui/components/button/LinkButton.tsx';
import { ResponsiveCard } from '@framework/ui/components/card/ResponsiveCard.tsx';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { DividerTitleElement } from '@framework/ui/components/text/DividerTitleElement.tsx';
import { useBreadcrumbItem } from '@framework/ui/core/router/breadcrumbs/BreadcrumbContext.tsx';
import {
  generatePath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { Button, Col, Row, Space } from 'antd';
import { type TableRowSelection } from 'antd/es/table/interface';
import Title from 'antd/es/typography/Title';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RouteComponentProps } from 'react-router';
import styled, { useTheme } from 'styled-components';
import { PageLayout } from '../../../ui/layout/PageLayout.tsx';
import { NetworkAlertPage } from '../../errors/network-alert/NetworkAlertPage.tsx';
import { checkCanEditAttorney } from './checkCanEditAttorney.ts';
import { AttorneyUserName } from './components/AttorneyUserName.tsx';
import { ChangeSubmissionManagerButton } from './components/ChangeSubmissionManagerButton.tsx';
import { DelegateUserName } from './components/DelegateUserName.tsx';
import { EditModulesDelegationsDrawer } from './components/EditModulesDelegationsDrawer.tsx';
import { RemoveSubmissionAttorneyButton } from './components/RemoveSubmissionAttorneyButton.tsx';
import { delegationColumns } from './delegationColumns.tsx';
import { useDelegationData } from './hooks/useDelegationData.ts';

function getSelectedModulesText(selectedRows: string[]) {
  if (selectedRows.length === 0) {
    return '';
  }
  if (selectedRows.length === 1) {
    return t(
      'selected-modules-text.un-modulo-selezionato',
      '1 modulo selezionato',
    );
  }
  return t(
    'selected-modules-text.moduli-selezionati',
    `{{selectedRows}} moduli selezionati`,
    { selectedRows: selectedRows.length },
  );
}

const StyledTitle = styled(Title)`
  &&& {
    font-size: ${({ theme }) => theme.fontSizeHeading3}px;
  }
`;

interface Params {
  id: string;
}

export type IDelegationPageProps = RouteComponentProps<Params>;

export function DelegationPage(props: IDelegationPageProps) {
  const history = useHistory();
  const user = useCurrentUser();
  const { t } = useTranslation();
  const theme = useTheme();
  const [submissionId] = useIdFromUrl(props, 'id');

  const { submission, title, inquiryRequest } = useSubmission(submissionId);
  const submissionModules = useSubmissionModules({ submissionId });

  const requiresFile = inquiryRequest.data?.necessitaDocumentoDelega ?? false;

  const { delegationData, isLoading, error } = useDelegationData({
    modules: submissionModules.modules,
    submissionId,
    applicant: submission?.datiTitolari,
  });

  const canEditAttorney = checkCanEditAttorney({
    delegationData,
    user,
    inquiry: inquiryRequest.data,
  });

  const canEditManager = DelegationMachine.can(
    delegationData,
    '.editManager',
    user,
  );
  const canEditDelegates = DelegationMachine.can(
    delegationData,
    '.editDelegates',
    user,
  );

  const openModulesDelegationsDrawer = useCallback(
    (selected: string[]) => {
      const data = delegationData.moduleDelegation.filter(delegation =>
        selected.includes(delegation.submissionModule.id ?? '<unknown>'),
      );

      setSelectedModules(data);
      setIsModulesDelegationsDrawerOpen(true);
    },
    [delegationData.moduleDelegation],
  );

  const buttonText = t(
    'delegation.page.delegation-table.button.gestisci',
    'Gestisci',
  );

  const tableColumns = useMemo(() => {
    const result = [...delegationColumns];
    if (canEditDelegates.allowed) {
      result.push({
        title: (
          <span className="sr-only">
            {t('delegation-page.azioni', 'Azioni')}
          </span>
        ),
        key: 'actions',
        align: 'right',
        render: (_, record) => (
          <Button
            onClick={() => {
              openModulesDelegationsDrawer([
                record.submissionModule.id ?? '<unknown>',
              ]);
            }}
          >
            {buttonText}
          </Button>
        ),
      });
    }
    return result;
  }, [canEditDelegates.allowed, openModulesDelegationsDrawer, buttonText, t]);

  useBreadcrumbItem({
    path: '/richieste',
    title: t('delegation.page.breadcrumb.richieste', 'Richieste'),
  });
  useBreadcrumbItem({
    path: generatePath('/richieste/:id', { id: submissionId ?? '<unknown>' }),
    title,
  });
  useBreadcrumbItem({
    path: history.location.pathname,
    title: t('delegation.page.breadcrumb.gestione-deleghe', 'Gestione Deleghe'),
  });

  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [isModulesDelegationsDrawerOpen, setIsModulesDelegationsDrawerOpen] =
    useState(false);
  const [selectedModules, setSelectedModules] = useState<IModuleDelegation[]>(
    [],
  );

  // accessibilità per la selezione in tabella
  const rowSelection: TableRowSelection<IModuleDelegation> = {
    type: 'checkbox',
    selectedRowKeys: selectedRows,
    onChange: selected => setSelectedRows(selected as string[]),
    columnTitle: checkboxNode => {
      if (React.isValidElement(checkboxNode)) {
        return React.cloneElement(checkboxNode, {
          'aria-label': t(
            'delegation.page.delegation-table.checkbox.seleziona-tutti-i-moduli',
            'Seleziona tutti i moduli',
          ),
        } as any);
      }
      return checkboxNode;
    },
    getCheckboxProps: record => ({
      'aria-label':
        t(
          'delegation.page.delegation-table.checkbox.seleziona-modulo',
          'Seleziona modulo',
        ) +
        ' ' +
        getModuleTitle(record.submissionModule),
      disabled: undefined,
    }),
  };

  // In anteprima di sola lettura la delega non è disponibile:
  // la pagina non deve essere raggiungibile nemmeno via URL diretto.
  // La guardia sta dopo tutti gli hook per poter interrompere il render.
  if (isInquiryReadonlyPreview(inquiryRequest.data) && submissionId) {
    history.replace(generatePath('/richieste/:id', { id: submissionId }));
    return null;
  }

  if (error) {
    if (error.response?.status === 403) {
      history.push('/richieste');
    }

    return (
      <NetworkAlertPage
        message={t(
          'delegation.page.error.impossibile-caricare-le-deleghe-della-richiesta',
          'Impossibile caricare le deleghe della richiesta',
        )}
        error={error}
      />
    );
  }

  return (
    <PageLayout>
      <ResponsiveCard
        hideCardHeader={theme.searchButtonOnTableFlag}
        loading={isLoading}
        title={
          <TitleSubmissionRow
            stateTag={<SubmissionStateTag submission={submission} />}
            right={
              <LinkButton
                icon={<ArrowLeftOutlined aria-hidden="true" />}
                size="large"
                to={generatePath('/richieste/:id', {
                  id: submissionId ?? '<unknown>',
                })}
              >
                {t(
                  'delegation-page.torna-alla-richiesta',
                  'Torna alla richiesta',
                )}
              </LinkButton>
            }
          >
            {title}
          </TitleSubmissionRow>
        }
      >
        <VerticalSpace />
        <FlexRow align="flex-start">
          <StyledTitle level={2}>
            {t('delegation-page.title', 'Gestione Deleghe')}
          </StyledTitle>
        </FlexRow>
        <div>
          <ResponsiveCard bordered size="small">
            {/* <Row style={{ display: 'flex', justifyContent: 'space-between' }}> */}
            <Row gutter={24}>
              <Col sm={24} md={12}>
                <div>
                  <div>{t('delegation-page.attorney', 'Procuratore')}</div>
                  <div>
                    {delegationData.attorney && (
                      <AttorneyUserName attorney={delegationData.attorney} />
                    )}
                    {canEditAttorney && (
                      <>
                        <ChangeSubmissionManagerButton
                          submissionId={submissionId}
                          iconOnly={delegationData.attorney != null}
                          type="attorney"
                          data={delegationData.attorney}
                          requiresFile={requiresFile}
                        />
                        {delegationData.attorney && (
                          <RemoveSubmissionAttorneyButton
                            submissionId={submissionId}
                            iconOnly
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Col>
              <Col sm={24} md={12}>
                <div>
                  <div>{t('delegation-page.manager', 'Gestore')}</div>
                  <div>
                    <DelegateUserName name={delegationData.manager?.name} />
                    {canEditManager.allowed && (
                      <ChangeSubmissionManagerButton
                        submissionId={submissionId}
                        iconOnly
                        type="manager"
                        data={delegationData.manager}
                      />
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </ResponsiveCard>
        </div>
        <VerticalSpace size={12} />
        <FlexRow
          right={
            canEditDelegates.allowed && (
              <Space>
                <div style={{ marginLeft: '12px' }}>
                  {getSelectedModulesText(selectedRows)}
                </div>
                <Button
                  type="default"
                  disabled={selectedRows.length === 0}
                  onClick={() => {
                    openModulesDelegationsDrawer(selectedRows);
                    setSelectedRows([]);
                  }}
                >
                  {t(
                    'delegation.page.button.gestisci-selezionati',
                    'Gestisci Selezionati',
                  )}
                </Button>
              </Space>
            )
          }
        >
          <DividerTitleElement
            title={t(
              'delegation.page.divider-title.deleghe-dei-moduli',
              'Deleghe dei Moduli',
            )}
          />
        </FlexRow>
        <TableData
          loading={isLoading}
          size="middle"
          columns={tableColumns}
          dataSource={delegationData.moduleDelegation}
          rowKey={record => record.submissionModule.id ?? '<unknown>'}
          rowSelection={canEditDelegates.allowed ? rowSelection : undefined}
          pagination={false}
        />
      </ResponsiveCard>
      <EditModulesDelegationsDrawer
        submissionId={submissionId ?? '<unknown>'}
        modulesDelegations={selectedModules}
        allDelegations={delegationData.moduleDelegation}
        open={isModulesDelegationsDrawerOpen}
        setOpen={setIsModulesDelegationsDrawerOpen}
      />
    </PageLayout>
  );
}
